import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Menu, X, User, LogOut, Settings, BarChart3, ChevronDown, Shield, FileText, Home, CheckCircle2, BadgeCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

export function Header() {
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  const { user, signOut, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowLogoutModal(true);
      setTimeout(() => {
        setShowLogoutModal(false);
        setLocation('/');
      }, 1500);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const navItems = [
    { href: '/', label: t('common.home'), icon: Home },
    ...(user ? [{ href: '/report', label: t('header.reportFraud'), icon: FileText }] : []),
    ...(isAdmin ? [{ href: '/analytics', label: t('common.analytics'), icon: BarChart3 }] : []),
    ...(isAdmin ? [{ href: '/admin', label: t('common.admin'), icon: Settings }] : []),
  ];

  const NavLink = ({ href, label, icon: Icon, mobile = false }: { href: string; label: string; icon: any; mobile?: boolean }) => (
    <Link
      href={href}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
        mobile ? "w-full" : "",
        location === href
          ? "bg-green-500/20 text-green-400"
          : "text-slate-300 hover:text-white hover:bg-slate-800"
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/50" />
      
      <nav className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link href="/">
            <a className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500/20 rounded-lg blur group-hover:bg-green-500/30 transition-colors" />
                <img 
                  src="/logo-transparent.png" 
                  alt="FraudCheck BD" 
                  className="relative h-9 w-9"
                  width="36"
                  height="36"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-white leading-tight">FraudCheck</span>
                <span className="text-[10px] font-medium text-green-400 tracking-wider">BANGLADESH</span>
              </div>
            </a>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink key={item.href} {...item} />
            ))}
          </div>

          {/* Right side - Auth buttons */}
          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg px-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                      {user.email?.charAt(0).toUpperCase()}
                    </div>
                    <span className="max-w-[120px] truncate text-sm">{user.email?.split('@')[0]}</span>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700 p-2">
                  <div className="px-2 py-2 mb-2 border-b border-slate-700">
                    <p className="text-sm font-medium text-white truncate">{user.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">{isAdmin ? t('header.administrator') : t('header.member')}</span>
                      {user.email_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-400">
                          <BadgeCheck className="h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-yellow-400">
                          <AlertCircle className="h-3 w-3" />
                          Unverified
                        </span>
                      )}
                    </div>
                  </div>
                  <DropdownMenuItem asChild className="text-slate-300 hover:text-white focus:bg-slate-700 rounded-md cursor-pointer">
                    <Link href="/dashboard">
                      <User className="h-4 w-4 mr-2" />
                      {t('common.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-slate-300 hover:text-white focus:bg-slate-700 rounded-md cursor-pointer">
                    <Link href="/profile">
                      <Settings className="h-4 w-4 mr-2" />
                      {t('common.settings')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-700 my-2" />
                      <DropdownMenuItem asChild className="text-slate-300 hover:text-white focus:bg-slate-700 rounded-md cursor-pointer">
                        <Link href="/analytics">
                          <BarChart3 className="h-4 w-4 mr-2" />
                          {t('common.analytics')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="text-slate-300 hover:text-white focus:bg-slate-700 rounded-md cursor-pointer">
                        <Link href="/admin">
                          <Shield className="h-4 w-4 mr-2" />
                          {t('common.admin')}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-slate-700 my-2" />
                  <DropdownMenuItem 
                    onClick={handleSignOut} 
                    className="text-red-400 hover:text-red-300 focus:bg-red-500/10 rounded-md cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  asChild 
                  className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg"
                >
                  <Link href="/login">{t('common.login')}</Link>
                </Button>
                <Button 
                  size="sm" 
                  asChild 
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg shadow-lg shadow-green-500/20"
                >
                  <Link href="/register">
                    <Shield className="h-4 w-4 mr-1" />
                    {t('header.getStarted')}
                  </Link>
                </Button>
              </div>
            )}

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="md:hidden text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={cn(
          "md:hidden overflow-hidden transition-all duration-300 ease-in-out",
          mobileMenuOpen ? "max-h-96 pb-4" : "max-h-0"
        )}>
          <div className="pt-4 space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.href} {...item} mobile />
            ))}
            
            {!user && (
              <div className="pt-4 mt-4 border-t border-slate-800 space-y-2">
                <Link
                  href="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full px-4 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  {t('common.login')}
                </Link>
                <Link
                  href="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full px-4 py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {t('header.getStarted')}
                </Link>
              </div>
            )}

            {user && (
              <div className="pt-4 mt-4 border-t border-slate-800 space-y-1">
                <Link
                  href="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  <User className="h-4 w-4" />
                  {t('common.dashboard')}
                </Link>
                <Link
                  href="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  <Settings className="h-4 w-4" />
                  {t('common.settings')}
                </Link>
                <button
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10"
                >
                  <LogOut className="h-4 w-4" />
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Logout Success Modal */}
      <Dialog open={showLogoutModal} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-800 border-slate-700 sm:max-w-sm text-center">
          <div className="flex flex-col items-center py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Logged Out Successfully</h3>
            <p className="text-slate-400">See you next time!</p>
            <div className="mt-4 w-full bg-slate-700 rounded-full h-1 overflow-hidden">
              <div className="bg-green-500 h-full w-full animate-pulse" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
